import React, { useRef, useState, useEffect, useMemo} from 'react';
import { motion, useInView } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLanguage } from '../context/LanguageContext';
import { faUsers, faClock, faGraduationCap, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import './Counter.css';

const Counter = ({ stats: customStats, isDark = false, showBar = true }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  // Statistiques par défaut (mémorisées pour éviter les recréations)
  const defaultStats = useMemo(() => [
    { icon: faUsers, target: 5, label: t.clients_satisfaits, suffix: '+', color: '#4FC3F7' },
    { icon: faClock, target: 2, label: t.annees_experience, suffix: '+', color: '#90EE90' },
    { icon: faGraduationCap, target: 50, label: t.nbre_formations, suffix: '+', color: '#FFB74D' },
    { icon: faProjectDiagram, target: 10, label: t.nbre_projets, suffix: '+', color: '#F06292' }
  ], [t]); // dépend de t pour les traductions

  const stats = customStats || defaultStats;

  const [counts, setCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    if (isInView) {
      const intervalIds = stats.map((stat, index) => {
        let current = 0;
        const increment = Math.ceil(stat.target / 50);
        return setInterval(() => {
          if (current < stat.target) {
            current += increment;
            if (current >= stat.target) {
              current = stat.target;
            }
            setCounts(prev => {
              const newCounts = [...prev];
              newCounts[index] = current;
              return newCounts;
            });
          }
        }, 40);
      });
      return () => intervalIds.forEach(id => clearInterval(id));
    }
  }, [isInView, stats]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
    }
  };

  return (
    <section className={`counter-section ${isDark ? 'dark' : ''}`} ref={ref}>
      <div className="container">
        <motion.div
          className="counter-grid"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {stats.map((stat, index) => (
            <motion.div key={index} className="counter-item" variants={itemVariants}>
              <div className="counter-icon" style={{ color: stat.color || '#4FC3F7' }}>
                <FontAwesomeIcon icon={stat.icon} />
              </div>
              <div className="counter-number">
                <span className="number">{counts[index]}</span>
                <span className="suffix" style={{ color: stat.color || '#4FC3F7' }}>
                  {stat.suffix || '+'}
                </span>
              </div>
              <h4>{stat.label}</h4>
              {/* Barre animée */}
              {showBar && (
                <motion.div
                  className="counter-bar"
                  initial={{ width: 0 }}
                  animate={isInView ? { width: '100%' } : { width: 0 }}
                  transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.4 + index * 0.1 }}
                  style={{ backgroundColor: stat.color || '#4FC3F7' }}
                />
              )}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Counter;